
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from '../../lib/redux/hooks';
import { LogoSpinnerComponent } from "../../lib/spinner/spinnerLogo";
import { EditIcon } from "../../ui/icons/edit";
import { TrashIcon } from "../../ui/icons/trash";
import { Dropdown } from "../../ui/organisms/dropdown";
import { Pagination } from "../../ui/organisms/pagination";
import { officeOptions, sot } from "../../utils";
import { RemoveUser, getUsers, selectedUserId, setFindAllUsersFiltersPage } from "./slice";
import './style.scss';

export function UsersList() {
    const usersState = useAppSelector(state => state.users)
    const dispatch = useAppDispatch()
    const navigate = useNavigate()

    useEffect(() => {
        dispatch(getUsers(usersState.filters))
    }, [dispatch, usersState.filters.page])

    if (usersState.getUsersStatus === 'loading') {
        return (
            <LogoSpinnerComponent fullScreen />
        )
    }

    return (
        <div className="w-full py-6 flex flex-col gap-4">
            <div className="w-full overflow-x-auto rounded-xl border border-neutral-200 shadow-sm">
                <table id="users-table" className="w-full border-collapse text-sm">
                    <thead>
                        <tr className="bg-brandPrimary-600 text-white uppercase text-xs tracking-wide h-[52px]">
                            <th className="px-3 py-3 text-left font-semibold whitespace-nowrap rounded-tl-xl">Nome Cognome</th>
                            <th className="px-3 py-3 text-left font-semibold whitespace-nowrap">Username</th>
                            <th className="px-3 py-3 text-left font-semibold whitespace-nowrap">Email</th>
                            <th className="px-3 py-3 text-left font-semibold whitespace-nowrap">Cellulare</th>
                            <th className="px-3 py-3 text-left font-semibold whitespace-nowrap">Ufficio</th>
                            <th className="px-3 py-3 text-left font-semibold whitespace-nowrap">Funzione</th>
                            <th className="px-3 py-3 text-left font-semibold whitespace-nowrap">Sede</th>
                            <th className="px-3 py-3 text-left font-semibold whitespace-nowrap">Status</th>
                            <th className="px-3 py-3 text-center font-semibold whitespace-nowrap rounded-tr-xl w-[60px]"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {usersState.getUsersResponse?.data.length === 0 ? (
                            <tr>
                                <td colSpan={13} className="px-3 py-3 text-center text-neutral-500 bg-white">
                                    {'Nessun utente trovato'}
                                </td>
                            </tr>
                        ) :
                            (usersState.getUsersResponse?.data || []).map((user, rowIndex) => (
                                <tr
                                    key={user.id}
                                    className={`border-b border-neutral-200 transition-colors duration-150 hover:bg-brandPrimary-50 ${rowIndex % 2 === 0 ? 'bg-white' : 'bg-neutral-50'}`}
                                >
                                    <td className="px-3 py-2 text-blue-800 font-bold text-left whitespace-nowrap">{user.name} {user.surname}</td>
                                    <td className="px-3 py-2 text-neutral-700 whitespace-nowrap">{user.username || '-'}</td>
                                    <td className="px-3 py-2 text-neutral-700 whitespace-nowrap">{user.email || '-'}</td>
                                    <td className="px-3 py-2 text-neutral-700 whitespace-nowrap">{user.phone || '-'}</td>
                                    <td className="px-3 py-2 whitespace-nowrap">
                                        {!user.office
                                            ? <span className="text-neutral-400">-</span>
                                            : officeOptions
                                                .filter(({ value }) => value === user.office)
                                                .map(el => (
                                                    <span key={el.value} className="inline-block bg-brandPrimary-500 text-white text-xs font-medium px-3 py-1 rounded-full">{el.label}</span>
                                                ))}
                                    </td>
                                    <td className="px-3 py-2 whitespace-nowrap">
                                        <span className="inline-block bg-brandPrimary-50 text-brandPrimary-600 text-xs font-semibold px-3 py-1 rounded-full">
                                            {user.role}
                                        </span>
                                    </td>
                                    <td className="px-3 py-2 whitespace-nowrap">
                                        {!user.headQuarterInfo
                                            ? <span className="text-neutral-400">-</span>
                                            : sot
                                                .filter(({ value }) => value === user.headQuarterInfo.id)
                                                .map(el => (
                                                    <span key={el.value} className="inline-block bg-brandPrimary-100 text-brandPrimary-800 text-xs font-medium px-3 py-1 rounded-full">{el.label}</span>
                                                ))}
                                    </td>
                                    <td className="px-3 py-2 whitespace-nowrap">
                                        {!user.deactivationDate ? (
                                            <span className="inline-block bg-green-100 text-green-700 text-xs font-semibold px-3 py-1 rounded-full">Attivo</span>
                                        ) : (
                                            <span className="inline-block bg-red-100 text-red-700 text-xs font-semibold px-3 py-1 rounded-full">Non Attivo</span>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 w-[60px] cursor-pointer">
                                        <div className="flex justify-center items-center">
                                            <Dropdown
                                                portaled
                                                items={[
                                                    {
                                                        label: 'Modifica',
                                                        icon: <EditIcon color={""} size={0} />,
                                                        onClick: () => {
                                                            dispatch(selectedUserId(user.id))
                                                            navigate('/edit-user/' + user.id)
                                                        }
                                                    },
                                                    {
                                                        label: 'Disattiva',
                                                        icon: <TrashIcon color={""} size={0} />,
                                                        onClick: () => {
                                                            dispatch(RemoveUser(user.id))
                                                            setTimeout(() => {
                                                                window.location.reload();
                                                            }, 800)
                                                        }
                                                    }
                                                ]}
                                            />
                                        </div>
                                    </td>
                                </tr>
                            ))}
                    </tbody>
                </table>
            </div>

            <Pagination
                numbResults={usersState.getUsersResponse ? usersState.getUsersResponse?.total : 0}
                detailsResults={'utenti'}
                pages={usersState.getUsersResponse?.totalPage || 1}
                currentPage={usersState.filters.page}
                setPage={(page) => dispatch(setFindAllUsersFiltersPage(page))}
            />
        </div>
    )
}